import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Mail, Instagram, Send, CheckCircle2, Loader2 } from "lucide-react";
import { SectionHeading } from "@/components/Produzioni";
import { CONTATTI, TIPI_RICHIESTA, TIPI_RICHIESTA_EN, STAMPA } from "@/data/content";
import { useLang } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS =
    "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-brand/60 focus:ring-2 focus:ring-brand/20";

export default function Contatti() {
    const { lang, ui } = useLang();
    const tipi = lang === "en" ? TIPI_RICHIESTA_EN : TIPI_RICHIESTA;
    const [form, setForm] = useState({ nome: "", email: "", tipo: TIPI_RICHIESTA[0], messaggio: "" });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/contatti`, form);
            setSent(true);
            toast.success(ui.contatti.toastOk);
        } catch (err) {
            toast.error(ui.contatti.toastErr);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contatti" data-testid="contatti-section" className="relative border-t border-white/5 bg-[#0a0a0d] px-5 py-24 sm:px-8 sm:py-32">
            <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-2">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE }}
                >
                    <SectionHeading
                        label={ui.contatti.label}
                        title={ui.contatti.title}
                        sub={ui.contatti.sub}
                        testid="contatti-heading"
                    />
                    <div className="-mt-6 space-y-5">
                        {[
                            { icon: MapPin, label: ui.contatti.sede, value: CONTATTI.sedi },
                            { icon: Mail, label: ui.contatti.email, value: CONTATTI.email },
                        ].map((row) => (
                            <div key={row.label} className="flex items-start gap-4">
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-brand/30 bg-brand/10 text-brand">
                                    <row.icon className="h-[18px] w-[18px]" />
                                </span>
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{row.label}</p>
                                    <p className="mt-1 text-sm font-medium text-white">{row.value}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 flex flex-wrap gap-3">
                        <a
                            data-testid="social-instagram-link"
                            href={STAMPA.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-slate-300 transition-all duration-300 hover:border-brand hover:text-brand"
                        >
                            <Instagram className="h-4 w-4" />
                            Instagram
                        </a>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, delay: 0.1, ease: EASE }}
                    className="rounded-3xl border border-white/10 bg-charcoal p-6 sm:p-9"
                >
                    {sent ? (
                        <div data-testid="contact-success-panel" className="flex h-full min-h-[420px] flex-col items-center justify-center text-center">
                            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand/15 text-brand">
                                <CheckCircle2 className="h-8 w-8" />
                            </span>
                            <h3 className="mt-6 font-display text-2xl font-bold text-white">{ui.contatti.successTitle}</h3>
                            <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">{ui.contatti.successText}</p>
                            <button
                                data-testid="contact-send-another-button"
                                onClick={() => {
                                    setSent(false);
                                    setForm({ nome: "", email: "", tipo: TIPI_RICHIESTA[0], messaggio: "" });
                                }}
                                className="mt-8 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-brand hover:text-brand"
                            >
                                {ui.contatti.successBtn}
                            </button>
                        </div>
                    ) : (
                        <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                        {ui.contatti.nome}
                                    </label>
                                    <input
                                        id="contact-name"
                                        data-testid="contact-name-input"
                                        required
                                        minLength={2}
                                        value={form.nome}
                                        onChange={update("nome")}
                                        placeholder={ui.contatti.phNome}
                                        className={INPUT_CLS}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                        Email
                                    </label>
                                    <input
                                        id="contact-email"
                                        data-testid="contact-email-input"
                                        required
                                        type="email"
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="mario@azienda.it"
                                        className={INPUT_CLS}
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="contact-type" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                    {ui.contatti.tipo}
                                </label>
                                <select
                                    id="contact-type"
                                    data-testid="contact-type-select"
                                    value={form.tipo}
                                    onChange={update("tipo")}
                                    className={`${INPUT_CLS} appearance-none bg-charcoal`}
                                >
                                    {tipi.map((t, i) => (
                                        <option key={t} value={TIPI_RICHIESTA[i]} className="bg-charcoal">
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                                    {ui.contatti.messaggio}
                                </label>
                                <textarea
                                    id="contact-message"
                                    data-testid="contact-message-input"
                                    required
                                    minLength={10}
                                    rows={5}
                                    value={form.messaggio}
                                    onChange={update("messaggio")}
                                    placeholder={ui.contatti.phMsg}
                                    className={`${INPUT_CLS} resize-none`}
                                />
                            </div>
                            <button
                                data-testid="contact-submit-button"
                                type="submit"
                                disabled={loading}
                                className="group flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-white shadow-[0_0_30px_rgba(244,28,51,0.3)] transition-all duration-300 hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        {ui.contatti.sending}
                                    </>
                                ) : (
                                    <>
                                        {ui.contatti.submit}
                                        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
